import { z } from 'zod'
import type { Service, Tag as TagType } from '~publish/gql/graphql'
import type { ListItem } from '../PostItems'
import { AttachmentTypes, Services } from '~publish/legacy/constants'

export type ValidHeaders = 'Text' | 'Tags' | 'Image URL'

// Shape produced after validating a CSV line
export type CsvLine = {
  [key in ValidHeaders]?: string
}

export type BulkPostSchemaOptions = {
  charLimit?: number
  requireText?: boolean
  requireImageUrl?: boolean
  requireTextOrImage?: boolean
}

export const createCsvLineSchema = ({
  charLimit = 500,
  requireText = false,
  requireImageUrl = false,
  requireTextOrImage = true,
}: BulkPostSchemaOptions = {}): z.ZodType<CsvLine, z.ZodTypeDef, unknown> => {
  const preprocessEmpty = (v: unknown): unknown =>
    typeof v === 'string' && v.trim() === '' ? undefined : v

  // TEXT field
  const textSchemaRequired = z
    .string({ required_error: 'Text is required' })
    .trim()
    .max(charLimit, `Text must not exceed ${charLimit} characters`)

  const textSchemaOptional = z
    .string()
    .trim()
    .max(charLimit, `Text must not exceed ${charLimit} characters`)

  const text = requireText
    ? z.preprocess(preprocessEmpty, textSchemaRequired)
    : z.preprocess(preprocessEmpty, textSchemaOptional.optional())

  // IMAGE URL field
  const imageSchemaRequired = z
    .string({ required_error: 'Image URL is required' })
    .trim()
    .url('Invalid image URL format')

  const imageSchemaOptional = z.string().trim().url('Invalid image URL format')

  const image = requireImageUrl
    ? z.preprocess(preprocessEmpty, imageSchemaRequired)
    : z.preprocess(preprocessEmpty, imageSchemaOptional.optional())

  const schema = z.object({
    Text: text,
    Tags: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
      z.string().optional(),
    ),
    'Image URL': image,
  })

  // ensure at least one when requested
  return requireTextOrImage
    ? schema.refine(
        (data) => Boolean(data.Text) || Boolean(data['Image URL']),
        { message: 'Either Text or Image URL must be provided' },
      )
    : schema
}

// You can still get type inference by using the schema from the function
export type CSVLine = z.infer<ReturnType<typeof createCsvLineSchema>>

const getValidationOptions = (
  channelService: Service,
): BulkPostSchemaOptions => {
  const service = Services.get(channelService)
  const charLimit =
    typeof service?.charLimit === 'function'
      ? service.charLimit(undefined)
      : service?.charLimit
  const requireText = service?.requiresText ?? false
  const requireImageUrl =
    service?.requiredAttachmentType === AttachmentTypes.MEDIA
  return {
    charLimit: charLimit ?? 500,
    requireText,
    requireImageUrl,
    requireTextOrImage: !requireText && !requireImageUrl,
  }
}

export const validateAndConstructItem = ({
  row,
  tags,
  channelId,
  channelService,
}: {
  row: CSVLine
  tags: TagType[]
  channelId: string
  channelService: Service
}): ListItem => {
  const validationSchema = createCsvLineSchema(
    getValidationOptions(channelService),
  )
  const validationResult = validationSchema.safeParse(row)
  const success = validationResult.success
  const errorMessages =
    validationResult.success === false
      ? validationResult.error.issues.map((issue) => issue.message)
      : []
  const warningMessages = []

  const rowTags = row.Tags
    ? row.Tags.split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : []
  const existingTagMap = new Map(tags.map((tag) => [tag.name, tag]))

  const { actualTags, missingTags } = rowTags.reduce<{
    actualTags: TagType[]
    missingTags: string[]
  }>(
    (acc, tagName) => {
      const found = existingTagMap.get(tagName)
      if (found) {
        acc.actualTags.push(found)
      } else {
        acc.missingTags.push(tagName)
      }
      return acc
    },
    { actualTags: [], missingTags: [] },
  )

  if (missingTags.length > 0) {
    warningMessages.push(`Excluded missing tags: ${missingTags.join(', ')}`)
  }
  return {
    id: crypto.randomUUID(),
    data: {
      profile_ids: [channelId],
      text: row.Text,
      media: row['Image URL'] ? { photo: row['Image URL'] } : undefined,
      tags: actualTags,
      // Always set created_source to bulk_upload here for BE Post Created tracking
      created_source: 'bulk_upload' as const,
    },
    status: success ? 'valid' : 'invalid',
    errors: errorMessages,
    warnings: warningMessages,
  }
}
